import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Checkbox } from './ui/checkbox'
import { ActionSearchBar } from './ui/action-search-bar'
import { cn } from '../lib/utils'
import { Globe, Train, Briefcase, ChevronDown, Search, Hexagon, ContactRound, Star, Plane, Users, Code, FileText, Clock, Coins, Flame, ListFilter } from 'lucide-react'
import type { LocationSuggestion } from '../hooks/useLocationSearch'

export interface JobFiltersState {
    workTypes: Set<string>
    hasSalary: boolean
    seniorities: Set<string>
    teams: Set<string>
    roleAttributes: Set<string>
    compensation: Set<string>
    selectedLocation: LocationSuggestion | null
    jobTitleSearch: string
    popularQuery: string | null
    stages: Set<string>
}

interface JobFiltersProps {
    filters: JobFiltersState
    onFiltersChange: (filters: JobFiltersState) => void
    availableTeams: string[]
    teamsLoading?: boolean
    selectedLocation: LocationSuggestion | null
    onLocationSelect: (location: LocationSuggestion | null) => void
    onFeedbackClick?: () => void
    activeCompanyCount?: number
}

export function JobFilters({ filters, onFiltersChange, availableTeams, teamsLoading, selectedLocation, onLocationSelect, onFeedbackClick, activeCompanyCount = 0 }: JobFiltersProps) {
    const [showWorkTypeDropdown, setShowWorkTypeDropdown] = useState(false)
    const [showSeniorityDropdown, setShowSeniorityDropdown] = useState(false)
    const [showTeamDropdown, setShowTeamDropdown] = useState(false)
    const [showRoleAttributesDropdown, setShowRoleAttributesDropdown] = useState(false)
    const [showCompensationDropdown, setShowCompensationDropdown] = useState(false)
    const [showPopularSearchesDropdown, setShowPopularSearchesDropdown] = useState(false)
    const [showMoreFiltersModal, setShowMoreFiltersModal] = useState(false)
    const [teamSearch, setTeamSearch] = useState('')

    const workTypeRef = useRef<HTMLDivElement>(null)
    const seniorityRef = useRef<HTMLDivElement>(null)
    const teamRef = useRef<HTMLDivElement>(null)
    const roleAttributesRef = useRef<HTMLDivElement>(null)
    const compensationRef = useRef<HTMLDivElement>(null)
    const popularSearchesRef = useRef<HTMLDivElement>(null)

    const workTypeOptions = [
        { value: 'remote', label: 'Remote', icon: Globe },
        { value: 'hybrid', label: 'Hybrid', icon: Train },
        { value: 'onsite', label: 'On-site', icon: Briefcase }
    ]

    const seniorityOptions = [
        { value: 'internship', label: 'Internship' },
        { value: 'entry', label: 'Entry Level' },
        { value: 'mid', label: 'Mid Level' },
        { value: 'senior', label: 'Senior' },
        { value: 'lead', label: 'Lead' },
        { value: 'principal', label: 'Principal' }
    ]

    const roleAttributeOptions = [
        { value: 'technical_role', label: 'Technical Role', icon: Code },
        { value: 'people_manager', label: 'People Manager', icon: Users },
        { value: 'travel_required', label: 'Travel Required', icon: Plane },
        { value: 'timezone_required', label: 'Timezone Required', icon: Clock }
    ]

    const compensationOptions = [
        { value: 'salary', label: 'Salary' },
        { value: 'equity', label: 'Equity' },
        { value: 'bonus', label: 'Bonus' },
        { value: 'variable_compensation', label: 'Variable Compensation' }
    ]

    const popularSearchesOptions = [
        { value: 'recent_funding', label: 'Recent Funding (Last 60 Days)' },
        { value: 'early_stage', label: 'Early-Stage Companies' },
        { value: 'remote_us', label: 'Remote in United States' },
        { value: 'ai_jobs', label: 'AI Job Titles' },
        { value: 'support_engineer', label: 'Support Engineer Jobs' }
    ]

    // Close dropdowns when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (workTypeRef.current && !workTypeRef.current.contains(event.target as Node)) {
                setShowWorkTypeDropdown(false)
            }
            if (seniorityRef.current && !seniorityRef.current.contains(event.target as Node)) {
                setShowSeniorityDropdown(false)
            }
            if (teamRef.current && !teamRef.current.contains(event.target as Node)) {
                setShowTeamDropdown(false)
            }
            if (roleAttributesRef.current && !roleAttributesRef.current.contains(event.target as Node)) {
                setShowRoleAttributesDropdown(false)
            }
            if (compensationRef.current && !compensationRef.current.contains(event.target as Node)) {
                setShowCompensationDropdown(false)
            }
            if (popularSearchesRef.current && !popularSearchesRef.current.contains(event.target as Node)) {
                setShowPopularSearchesDropdown(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const toggleWorkType = (workType: string) => {
        const newWorkTypes = new Set(filters.workTypes)
        if (newWorkTypes.has(workType)) {
            newWorkTypes.delete(workType)
        } else {
            newWorkTypes.add(workType)
        }
        onFiltersChange({ ...filters, workTypes: newWorkTypes })
    }

    const removeWorkType = (workType: string) => {
        const newWorkTypes = new Set(filters.workTypes)
        newWorkTypes.delete(workType)
        onFiltersChange({ ...filters, workTypes: newWorkTypes })
    }

    const toggleSeniority = (seniority: string) => {
        const newSeniorities = new Set(filters.seniorities)
        if (newSeniorities.has(seniority)) {
            newSeniorities.delete(seniority)
        } else {
            newSeniorities.add(seniority)
        }
        onFiltersChange({ ...filters, seniorities: newSeniorities })
    }

    const removeSeniority = (seniority: string) => {
        const newSeniorities = new Set(filters.seniorities)
        newSeniorities.delete(seniority)
        onFiltersChange({ ...filters, seniorities: newSeniorities })
    }

    const toggleTeam = (team: string) => {
        const newTeams = new Set(filters.teams)
        if (newTeams.has(team)) {
            newTeams.delete(team)
        } else {
            newTeams.add(team)
        }
        onFiltersChange({ ...filters, teams: newTeams })
    }

    const removeTeam = (team: string) => {
        const newTeams = new Set(filters.teams)
        newTeams.delete(team)
        onFiltersChange({ ...filters, teams: newTeams })
    }

    const toggleRoleAttribute = (attribute: string) => {
        const newRoleAttributes = new Set(filters.roleAttributes)
        if (newRoleAttributes.has(attribute)) {
            newRoleAttributes.delete(attribute)
        } else {
            newRoleAttributes.add(attribute)
        }
        onFiltersChange({ ...filters, roleAttributes: newRoleAttributes })
    }

    const removeRoleAttribute = (attribute: string) => {
        const newRoleAttributes = new Set(filters.roleAttributes)
        newRoleAttributes.delete(attribute)
        onFiltersChange({ ...filters, roleAttributes: newRoleAttributes })
    }

    const toggleCompensation = (compensation: string) => {
        const newCompensation = new Set(filters.compensation)
        if (newCompensation.has(compensation)) {
            newCompensation.delete(compensation)
        } else {
            newCompensation.add(compensation)
        }
        onFiltersChange({ ...filters, compensation: newCompensation })
    }

    const removeCompensation = (compensation: string) => {
        const newCompensation = new Set(filters.compensation)
        newCompensation.delete(compensation)
        onFiltersChange({ ...filters, compensation: newCompensation })
    }

    const hasActiveFilters = filters.workTypes.size > 0 || filters.seniorities.size > 0 || filters.teams.size > 0 || filters.roleAttributes.size > 0 || filters.compensation.size > 0 || filters.selectedLocation !== null

    // Filter teams based on search and sort selected teams to the top
    const filteredTeams = availableTeams
        .filter(team => team.toLowerCase().includes(teamSearch.toLowerCase()))
        .sort((a, b) => {
            const aSelected = filters.teams.has(a)
            const bSelected = filters.teams.has(b)
            if (aSelected && !bSelected) return -1
            if (!aSelected && bSelected) return 1
            return 0
        })

    return (
        <div className="space-y-3">
            {/* Stats */}
            <div className="mb-3 sm:mb-4">
                <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-600">
                    <Link
                        to="/company/directory"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:underline transition-colors"
                    >
                        {activeCompanyCount.toLocaleString()} active companies
                    </Link>
                    <span className="text-gray-300">|</span>
                    <span>updated every hour</span>
                    <span className="text-gray-300">|</span>
                    <Link
                        to="/roadmap"
                        className="text-blue-600 hover:text-blue-700 hover:underline transition-colors"
                    >
                        submit feedback
                    </Link>
                </div>
            </div>

            {/* Search Bars */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2 sm:mb-3">
                {/* Job Title Search */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-700" />
                    <input
                        type="text"
                        placeholder="Search job titles..."
                        value={filters.jobTitleSearch}
                        onChange={(e) => onFiltersChange({ ...filters, jobTitleSearch: e.target.value })}
                        className="w-full pl-9 sm:pl-10 pr-9 sm:pr-10 py-1.5 sm:py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs sm:text-sm placeholder:text-gray-700"
                    />
                </div>

                {/* Location Search */}
                <ActionSearchBar
                    onLocationSelect={onLocationSelect}
                    selectedLocation={selectedLocation}
                />
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                {/* Model Dropdown */}
                <div ref={workTypeRef} className="relative">
                    <button
                        onClick={() => setShowWorkTypeDropdown(!showWorkTypeDropdown)}
                        className={cn(
                            "inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-normal transition-colors",
                            "border border-gray-200 min-w-[95px]",
                            filters.workTypes.size > 0
                                ? "bg-gray-100 text-gray-900 border-gray-300"
                                : "bg-white text-gray-700"
                        )}
                    >
                        <Globe className="w-3 h-3" />
                        <span>Model</span>
                        {filters.workTypes.size > 0 && (
                            <span className="ml-0.5 sm:ml-1 px-1 sm:px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-900 text-xs">
                                {filters.workTypes.size}
                            </span>
                        )}
                        <ChevronDown className={cn(
                            "w-3 h-3 transition-transform",
                            showWorkTypeDropdown && "rotate-180"
                        )} />
                    </button>

                    {showWorkTypeDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-48 rounded-lg border border-gray-200 bg-white shadow-lg z-10">
                            <div className="p-2 space-y-1">
                                {workTypeOptions.map(({ value, label }) => (
                                    <label
                                        key={value}
                                        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <Checkbox
                                            checked={filters.workTypes.has(value)}
                                            onCheckedChange={() => toggleWorkType(value)}
                                        />
                                        <span className="text-sm text-gray-700">
                                            {label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Team Filter Dropdown with Search */}
                <div ref={teamRef} className="relative">
                    <button
                        onClick={() => setShowTeamDropdown(!showTeamDropdown)}
                        className={cn(
                            "inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-normal transition-colors",
                            "border border-gray-200 min-w-[85px]",
                            filters.teams.size > 0
                                ? "bg-gray-100 text-gray-900 border-gray-300"
                                : "bg-white text-gray-700"
                        )}
                    >
                        <ContactRound className="w-3 h-3" />
                        Team
                        {filters.teams.size > 0 && (
                            <span className="ml-0.5 sm:ml-1 px-1 sm:px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-900 text-xs">
                                {filters.teams.size}
                            </span>
                        )}
                        <ChevronDown className={cn(
                            "w-3 h-3 transition-transform",
                            showTeamDropdown && "rotate-180"
                        )} />
                    </button>

                    {showTeamDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-72 rounded-lg border border-gray-200 bg-white shadow-lg z-10">
                            {/* Search Input */}
                            <div className="p-2 border-b border-gray-100">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search teams..."
                                        value={teamSearch}
                                        onChange={(e) => setTeamSearch(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm rounded-md border border-gray-200 bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        onClick={(e) => e.stopPropagation()}
                                    />
                                </div>
                            </div>

                            {/* Team List */}
                            <div className="max-h-64 overflow-y-auto">
                                {teamsLoading ? (
                                    <div className="p-4 text-center text-sm text-gray-500">
                                        Loading teams...
                                    </div>
                                ) : filteredTeams.length > 0 ? (
                                    <div className="p-2 space-y-1">
                                        {filteredTeams.map((team) => (
                                            <label
                                                key={team}
                                                className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                                            >
                                                <Checkbox
                                                    checked={filters.teams.has(team)}
                                                    onCheckedChange={() => toggleTeam(team)}
                                                />
                                                <span className="text-sm text-gray-700">
                                                    {team}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-4 text-center text-sm text-gray-500">
                                        {teamSearch ? 'No teams found' : 'No teams available'}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Level Dropdown */}
                <div ref={seniorityRef} className="relative">
                    <button
                        onClick={() => setShowSeniorityDropdown(!showSeniorityDropdown)}
                        className={cn(
                            "inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-normal transition-colors",
                            "border border-gray-200 min-w-[85px]",
                            filters.seniorities.size > 0
                                ? "bg-gray-100 text-gray-900 border-gray-300"
                                : "bg-white text-gray-700"
                        )}
                    >
                        <Hexagon className="w-3 h-3" />
                        <span>Level</span>
                        {filters.seniorities.size > 0 && (
                            <span className="ml-0.5 sm:ml-1 px-1 sm:px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-900 text-xs">
                                {filters.seniorities.size}
                            </span>
                        )}
                        <ChevronDown className={cn(
                            "w-3 h-3 transition-transform",
                            showSeniorityDropdown && "rotate-180"
                        )} />
                    </button>

                    {showSeniorityDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-48 rounded-lg border border-gray-200 bg-white shadow-lg z-10">
                            <div className="p-2 space-y-1">
                                {seniorityOptions.map(({ value, label }) => (
                                    <label
                                        key={value}
                                        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <Checkbox
                                            checked={filters.seniorities.has(value)}
                                            onCheckedChange={() => toggleSeniority(value)}
                                        />
                                        <span className="text-sm text-gray-700">
                                            {label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Details Dropdown */}
                <div ref={roleAttributesRef} className="relative">
                    <button
                        onClick={() => setShowRoleAttributesDropdown(!showRoleAttributesDropdown)}
                        className={cn(
                            "inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-normal transition-colors",
                            "border border-gray-200 min-w-[95px]",
                            filters.roleAttributes.size > 0
                                ? "bg-gray-100 text-gray-900 border-gray-300"
                                : "bg-white text-gray-700"
                        )}
                    >
                        <Star className="w-3 h-3" />
                        <span>Details</span>
                        {filters.roleAttributes.size > 0 && (
                            <span className="ml-0.5 sm:ml-1 px-1 sm:px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-900 text-xs">
                                {filters.roleAttributes.size}
                            </span>
                        )}
                        <ChevronDown className={cn(
                            "w-3 h-3 transition-transform",
                            showRoleAttributesDropdown && "rotate-180"
                        )} />
                    </button>

                    {showRoleAttributesDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-52 rounded-lg border border-gray-200 bg-white shadow-lg z-10">
                            <div className="p-2 space-y-1">
                                {roleAttributeOptions.map(({ value, label }) => (
                                    <label
                                        key={value}
                                        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <Checkbox
                                            checked={filters.roleAttributes.has(value)}
                                            onCheckedChange={() => toggleRoleAttribute(value)}
                                        />
                                        <span className="text-sm text-gray-700">
                                            {label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Pay Filter */}
                <div ref={compensationRef} className="relative">
                    <button
                        onClick={() => setShowCompensationDropdown(!showCompensationDropdown)}
                        className={cn(
                            "inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-normal transition-colors",
                            "border border-gray-200 min-w-[75px]",
                            filters.compensation.size > 0
                                ? "bg-gray-100 text-gray-900 border-gray-300"
                                : "bg-white text-gray-700"
                        )}
                    >
                        <Coins className="w-3 h-3" />
                        <span>Pay</span>
                        {filters.compensation.size > 0 && (
                            <span className="ml-0.5 sm:ml-1 px-1 sm:px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-900 text-xs">
                                {filters.compensation.size}
                            </span>
                        )}
                        <ChevronDown className={cn(
                            "w-3 h-3 transition-transform",
                            showCompensationDropdown && "rotate-180"
                        )} />
                    </button>

                    {showCompensationDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-60 rounded-lg border border-gray-200 bg-white shadow-lg z-10">
                            <div className="p-2 space-y-1">
                                {compensationOptions.map(({ value, label }) => (
                                    <label
                                        key={value}
                                        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                                    >
                                        <Checkbox
                                            checked={filters.compensation.has(value)}
                                            onCheckedChange={() => toggleCompensation(value)}
                                        />
                                        <span className="text-sm text-gray-700">
                                            {label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Popular Dropdown */}
                <div ref={popularSearchesRef} className="relative">
                    <button
                        onClick={() => setShowPopularSearchesDropdown(!showPopularSearchesDropdown)}
                        className={cn(
                            "inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs sm:text-sm font-normal transition-colors",
                            "border border-gray-200 min-w-[100px]",
                            filters.popularQuery
                                ? "bg-gray-100 text-gray-900 border-gray-300"
                                : "bg-white text-gray-700"
                        )}
                    >
                        <Flame className="w-3 h-3" />
                        <span>Popular</span>
                        {filters.popularQuery && (
                            <span className="ml-0.5 sm:ml-1 px-1 sm:px-1.5 py-0.5 rounded-full bg-gray-200 text-gray-900 text-xs">
                                1
                            </span>
                        )}
                        <ChevronDown className={cn(
                            "w-3 h-3 transition-transform",
                            showPopularSearchesDropdown && "rotate-180"
                        )} />
                    </button>

                    {showPopularSearchesDropdown && (
                        <div className="absolute top-full left-0 mt-2 w-64 rounded-lg border border-gray-200 bg-white shadow-lg z-10">
                            <div className="p-2 space-y-1">
                                {popularSearchesOptions.map(({ value, label }) => (
                                    <button
                                        key={value}
                                        onClick={() => {
                                            onFiltersChange({ ...filters, popularQuery: value })
                                            setShowPopularSearchesDropdown(false)
                                        }}
                                        className={cn(
                                            "w-full text-left px-3 py-2 rounded-md transition-colors",
                                            filters.popularQuery === value
                                                ? "bg-gray-100 text-gray-900"
                                                : "hover:bg-gray-50 text-gray-700"
                                        )}
                                    >
                                        <span className="text-sm">
                                            {label}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

            </div>

            {/* More Filters Modal */}
            {showMoreFiltersModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowMoreFiltersModal(false)}>
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">More Filters</h2>
                            <button
                                onClick={() => setShowMoreFiltersModal(false)}
                                className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="text-center py-8">
                            <p className="text-gray-600">Coming soon...</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
